// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import { ImageSettingKey } from "@rpsc/MobiusOverrides/LazyImage";
import * as React from "react";

// XNG-Changes
enum fields {
    isFullWidth = "isFullWidth",
    contentBgColor = "contentBgColor",
    override = "override",
    overrideSmallImage = "overrideSmallImage",
    overrideMediumImage = "overrideMediumImage",
    overrideLargeImage = "overrideLargeImage",
    imageHeight = "imageHeight",
}

interface Props extends WidgetProps, HasCategoryContext {
    fields: {
        [fields.isFullWidth]: boolean;
        [fields.contentBgColor]: string;
        [fields.override]: boolean;
        [fields.overrideSmallImage]: string;
        [fields.overrideMediumImage]: string;
        [fields.overrideLargeImage]: string;
        [fields.imageHeight]: string;
    };
}

export interface CategoryDetailBannerStyles {
    container?: InjectableCss;
}

export const bannerStyles: CategoryDetailBannerStyles = {};

const styles = bannerStyles;

const CategoryDetailBanner = ({ category, fields }: Props) => {
    const { isTabletScreen, isMobileScreen } = useAppSelector(state => (state as CustomAppState).xCommon);
    if (!category || !category.name) {
        return null;
    }
    const { isFullWidth, contentBgColor, override, overrideSmallImage, overrideMediumImage, overrideLargeImage } =
        fields;
    const largeBackgroundImage = override
        ? overrideLargeImage || overrideMediumImage
        : category.largeImagePath || category.smallImagePath;
    const mediumBackgroundImage = override ? overrideMediumImage : category.largeImagePath || category.smallImagePath;
    const smallBackgroundImage = override
        ? overrideSmallImage || overrideMediumImage
        : category.smallImagePath || category.largeImagePath;
    const contentImage = isTabletScreen
        ? isMobileScreen
            ? smallBackgroundImage
            : mediumBackgroundImage
        : largeBackgroundImage;
    // XNG-Changes: use XngBanner
    return (
        <StyledWrapper
            {...styles.container}
            data-test-selector="categoryDetailBanner_htmlContent"
            {...{ isFullWidth, contentBgColor }}
        >
            {/* {parse(category.htmlContent, parserOptions)} */}
            {createWidgetElement("XngBanner/XngBanner", {
                fields: {
                    imageContent: !isFullWidth ? contentImage : "",
                    imageContentPosition: "right",
                    containerGapForSmallScreens: "12px",
                    title: category.name,
                    description: category.htmlContent,
                    textContentMaxWidth: "50%",
                    ...(isFullWidth && {
                        largeBackgroundImage,
                        mediumBackgroundImage,
                        smallBackgroundImage,
                        contentBackgroundColor: contentBgColor,
                        contentBorderRadius: "2px",
                        textContentMaxWidth: "753px",
                        contentPaddingForLargeScreens: "16px",
                        contentMarginForLargeScreens: "32px 16px",
                        contentMarginForSmallScreens: "0px",
                        containerGapForSmallScreens: "0px",
                        maxImageContentHeight: fields.imageHeight,
                    }),
                },
                imageKey: ImageSettingKey.category,
            } as any)}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailBanner),
    definition: {
        group: "Category Details",
        icon: "image-landscape",
        allowedContexts: ["CategoryDetailsPage", "ProductListPage"],
        // XNG-Changes
        fieldDefinitions: [
            {
                name: fields.isFullWidth,
                displayName: "Full Width Image",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.contentBgColor,
                displayName: "Content Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#DAE7F1",
                fieldType: "General",
                isVisible: item => item.fields.isFullWidth,
            },
            {
                name: fields.override,
                displayName: "Override",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.overrideSmallImage,
                displayName: "Mobile Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: item => item.fields.override,
                fieldType: "General",
            },
            {
                name: fields.overrideMediumImage,
                displayName: "Tablet Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: item => item.fields.override,
                isRequired: true,
                fieldType: "General",
            },
            {
                name: fields.overrideLargeImage,
                displayName: "Desktop Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: item => item.fields.override,
                fieldType: "General",
            },
            {
                name: fields.imageHeight,
                displayName: "Image Height",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "450px",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("height", value) ? null : `Invalid Input`;
                },
                isVisible: item => item.fields.isFullWidth,
            },
        ],
    },
};

export default widgetModule;
